import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Chip, Button, Divider,
  LinearProgress, Alert, List, ListItem, ListItemText, ListItemSecondaryAction,
  Avatar, Tooltip, Skeleton, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Warning, Error as ErrorIcon, CheckCircle, Description,
  ArrowForward, Refresh, AttachMoney
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { contractsApi } from '../services/api';
import { format, parseISO } from 'date-fns';
import type { DashboardStats, ObligationStats } from '../types';

const COLORS = ['#1565c0', '#0288d1', '#00897b', '#558b2f', '#f57f17', '#e53935'];

/** Per-status colour palette */
const STATUS_COLORS: Record<string, string> = {
  active:     '#2e7d32',
  draft:      '#757575',
  suspended:  '#ed6c02',
  expired:    '#d32f2f',
  terminated: '#7b1fa2',
};

function StatCard({ title, value, subtitle, color, icon, onClick }: any) {
  return (
    <Card sx={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
            <Typography variant="h4" fontWeight={700} color={color || 'text.primary'}>
              {value ?? <Skeleton width={60} />}
            </Typography>
            {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
          </Box>
          <Avatar sx={{ bgcolor: `${color || 'primary'}.light`, color: color || 'primary.main', width: 48, height: 48 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
}

function ExpirationChip({ days }: { days: number }) {
  if (days < 0) return <Chip label="Expired" color="error" size="small" />;
  if (days <= 7) return <Chip label={`${days}d`} color="error" size="small" />;
  if (days <= 30) return <Chip label={`${days}d`} color="warning" size="small" />;
  if (days <= 90) return <Chip label={`${days}d`} color="info" size="small" />;
  return <Chip label={`${days}d`} size="small" />;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await contractsApi.dashboard();
      setData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDashboard(); }, []);

  const stats: DashboardStats = data?.stats || {};
  const obligationStats: ObligationStats = data?.obligationStats || {};
  const byType = data?.byType || [];
  const byStatusType: Array<{ contract_type: string; status: string; count: number }> = data?.byStatusType || [];
  const upcomingExpirations = data?.upcomingExpirations || [];

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(val);

  // ── Status pie chart state ──────────────────────────────────────
  const [statusTypeFilter, setStatusTypeFilter] = useState<string>('All Types');

  /** Contract types that have at least one row in byStatusType */
  const availableTypes = useMemo(() => {
    const types = Array.from(new Set(byStatusType.map(d => d.contract_type))).sort();
    return ['All Types', ...types];
  }, [byStatusType]);

  /** Aggregate counts by status for the selected contract type */
  const statusPieData = useMemo(() => {
    const rows = statusTypeFilter === 'All Types'
      ? byStatusType
      : byStatusType.filter(d => d.contract_type === statusTypeFilter);
    const agg: Record<string, number> = {};
    for (const row of rows) {
      agg[row.status] = (agg[row.status] || 0) + row.count;
    }
    return Object.entries(agg)
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count);
  }, [byStatusType, statusTypeFilter]);

  const statusPieTotal = statusPieData.reduce((s, d) => s + d.count, 0);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5">Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={loadDashboard} disabled={loading} variant="outlined" size="small">
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* Key Metrics */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Contracts"
            value={stats.active_contracts}
            subtitle={`${stats.total_contracts || 0} total`}
            color="primary"
            icon={<Description />}
            onClick={() => navigate('/contracts?status=active')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Expiring in 30 Days"
            value={stats.expiring_30_days}
            subtitle={`${stats.expiring_90_days || 0} within 90 days`}
            color="warning"
            icon={<Warning />}
            onClick={() => navigate('/reports?tab=renewals')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Overdue Obligations"
            value={obligationStats.overdue_obligations}
            subtitle={`${obligationStats.at_risk_obligations || 0} at risk`}
            color="error"
            icon={<ErrorIcon />}
            onClick={() => navigate('/obligations?status=overdue')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Contract Value"
            value={stats.total_contract_value ? formatCurrency(stats.total_contract_value) : '$0'}
            subtitle="Active contracts"
            color="success"
            icon={<AttachMoney />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Upcoming Expirations */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Upcoming Contract Expirations</Typography>
                <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/reports?tab=renewals')}>
                  View All
                </Button>
              </Box>
              {upcomingExpirations.length === 0 && !loading ? (
                <Box py={3} textAlign="center">
                  <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography color="text.secondary">No contracts expiring in the next 90 days</Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {upcomingExpirations.map((contract: any, i: number) => (
                    <React.Fragment key={contract.id}>
                      {i > 0 && <Divider />}
                      <ListItem
                        sx={{ px: 0, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1 }}
                        onClick={() => navigate(`/contracts/${contract.id}`)}
                      >
                        <ListItemText
                          primary={<Typography variant="body2" fontWeight={500}>{contract.contract_name}</Typography>}
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {contract.counterparty_name} · Expires {format(parseISO(contract.expiration_date), 'MMM d, yyyy')}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <ExpirationChip days={contract.days_remaining} />
                        </ListItemSecondaryAction>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={5}>
          {/* Contracts by Type Pie Chart */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Contracts by Type</Typography>
              {byType.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={byType} dataKey="count" nameKey="contract_type" cx="50%" cy="50%" outerRadius={70} label={({ contract_type, count }) => `${contract_type} (${count})`}>
                      {byType.map((_: any, index: number) => (
                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <ReTooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box py={2} textAlign="center">
                  <Typography variant="body2" color="text.secondary">No data</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Contracts by Status (filterable by type) */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="h6">Contracts by Status</Typography>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Contract Type</InputLabel>
                  <Select
                    value={statusTypeFilter}
                    label="Contract Type"
                    onChange={e => setStatusTypeFilter(e.target.value)}
                  >
                    {availableTypes.map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {statusPieData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={170}>
                    <PieChart>
                      <Pie
                        data={statusPieData}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        innerRadius={42}
                        outerRadius={72}
                        paddingAngle={2}
                        label={({ status, count, percent }) =>
                          percent > 0.08 ? `${Math.round(percent * 100)}%` : ''
                        }
                        labelLine={false}
                      >
                        {statusPieData.map((entry, index) => (
                          <Cell
                            key={entry.status}
                            fill={STATUS_COLORS[entry.status] ?? COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <ReTooltip
                        formatter={(value: number, name: string) => [
                          `${value} (${statusPieTotal ? Math.round((value / statusPieTotal) * 100) : 0}%)`,
                          name.charAt(0).toUpperCase() + name.slice(1)
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>

                  {/* Legend */}
                  <Box display="flex" flexWrap="wrap" gap={1.5} justifyContent="center" mt={0.5}>
                    {statusPieData.map(entry => (
                      <Box key={entry.status} display="flex" alignItems="center" gap={0.5}>
                        <Box
                          sx={{
                            width: 10, height: 10, borderRadius: '50%',
                            bgcolor: STATUS_COLORS[entry.status] ?? '#999',
                            flexShrink: 0
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {entry.status}
                        </Typography>
                        <Typography variant="caption" fontWeight={700}>
                          {entry.count}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Total */}
                  <Box textAlign="center" mt={0.75}>
                    <Typography variant="caption" color="text.disabled">
                      {statusPieTotal} contract{statusPieTotal !== 1 ? 's' : ''}
                      {statusTypeFilter !== 'All Types' ? ` · ${statusTypeFilter}` : ''}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box py={2} textAlign="center">
                  <Typography variant="body2" color="text.secondary">No data</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Obligation Status Summary */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Obligations Summary</Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                {[
                  { label: 'Total', value: obligationStats.total_obligations, color: 'text.primary' },
                  { label: 'Completed', value: obligationStats.completed_obligations, color: 'success.main' },
                  { label: 'Overdue', value: obligationStats.overdue_obligations, color: 'error.main' },
                  { label: 'At Risk', value: obligationStats.at_risk_obligations, color: 'warning.main' }
                ].map(item => (
                  <Box key={item.label} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                    <Typography variant="body2" fontWeight={600} color={item.color}>{item.value ?? 0}</Typography>
                  </Box>
                ))}
                <Divider />
                <Button size="small" onClick={() => navigate('/obligations')} fullWidth variant="outlined">
                  View All Obligations
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
